// Reading what each part of marketing reports, for Performance and Home.
// Each read is kept for a few minutes, so going from Home to Performance
// (or between tabs) doesn't ask again. A part that can't answer resolves to
// null, and the page says what's coming instead of showing a zero.
//
// Reads are kept per account and location: signing out doesn't reload the
// app, so whoever signs in next must never see these numbers.
import { marketingApiService } from '../../../services/marketingApiService'
import { marketingBackend, isServerId } from '../../../services/marketingBackend'
import { giftCardService } from '../../../../../_services/giftCard.service'
import { reviewService } from '../../../../../_services/review.service'
import { hydrateJourneys } from '../../../../marketing/journeys/journeysStore'
import { getSchemeStats, hydrateLoyalty, currentOwnerId } from '../../../../loyalty-v2/loyaltyStore'
import { loadConversations, loadSettings } from '../../../../agent/frontdesk/api'
import { checkTimes } from '../../social/data/freeSlots'
import { sendsFromApi, endOfDay } from './results'

const MINUTE = 60 * 1000
const cache = new Map()
const who = () => `${localStorage.getItem('userId') || ''}:${localStorage.getItem('salonId') || ''}`
const cached = (key, load, ttl = 3 * MINUTE) => {
  const id = `${who()}|${key}`
  const hit = cache.get(id)
  if (hit && Date.now() - hit.at < ttl) return hit.job
  const job = Promise.resolve().then(load)
  cache.set(id, { at: Date.now(), job })
  job.catch(() => cache.delete(id))
  return job
}

const day = d => (d ? new Date(d).toISOString().slice(0, 10) : 'all')
const span = range => `${day(range.start)}:${day(range.end)}`
const iso = d => (d ? new Date(d).toISOString() : undefined)
const time = (at) => { const t = at ? new Date(at).getTime() : NaN; return Number.isNaN(t) ? null : t }

// Sent emails and texts, most recent first: up to 250 of each
export const loadSends = (dispatch, salonIds = null) => cached(`sends:${(salonIds || []).join(',')}`, async () => {
  const where = salonIds && salonIds.length ? { salonIds } : {}
  const all = async (type) => {
    const out = []
    for (let page = 1; page <= 5; page += 1) {
      // eslint-disable-next-line no-await-in-loop
      const r = await marketingApiService.getCampaignsPaged(dispatch, { type, status: 'sent', currentPage: page, limit: 50, ...where })
      out.push(...sendsFromApi(r.campaigns, type))
      if (!r.hasMore) break
    }
    return out
  }
  const [email, sms] = await Promise.all([all('email'), all('sms')])
  return [...email, ...sms]
})

// Money the server has put against marketing (null until it reports it)
// salonIds: the header Location picker's selection (array), sent only when it
// spans more than this location; the server scopes it to their own sites.
const scope = salonIds => (salonIds && salonIds.length ? { salonIds: salonIds.join(',') } : {})

export const loadOverview = (dispatch, range, salonIds = null) => cached(`overview:${span(range)}:${(salonIds || []).join(',')}`, () => marketingApiService
  .getAnalyticsOverview(dispatch, { startDate: iso(range.start), endDate: range.end ? iso(endOfDay(range.end)) : undefined, ...scope(salonIds) })
  .then(r => (r && typeof r === 'object' ? r : null))
  .catch(() => null))

// Reminders and the other automations (null until analytics/automations answers)
export const loadAutomations = (dispatch, range, salonIds = null) => cached(`automations:${span(range)}:${(salonIds || []).join(',')}`, () => marketingApiService
  .getAutomationStats(dispatch, { startDate: iso(range.start), endDate: range.end ? iso(endOfDay(range.end)) : undefined, ...scope(salonIds) })
  .then(r => (r && Array.isArray(r.automations) ? r : null))
  .catch(() => null))

// Gift vouchers sold in the range, with the period before
export const loadVouchers = range => cached(`vouchers:${span(range)}`, () => giftCardService
  .getSalesDashboard({ startDate: iso(range.start), endDate: range.end ? iso(endOfDay(range.end)) : undefined })
  .catch(() => null))

// Reviews back to a date, for each location
export const loadReviewsSince = (since, salonIds = null) => {
  const ids = salonIds && salonIds.length ? salonIds : [localStorage.getItem('salonId')]
  return cached(`reviews:${day(since)}:${ids.join(',')}`, async () => {
    const out = []
    // eslint-disable-next-line no-restricted-syntax
    for (const id of ids) {
      for (let skip = 0, page = 0; page < 10; page += 1) {
        // eslint-disable-next-line no-await-in-loop
        const resp = await reviewService.getSalonReviews(id, skip, 20)
        const data = (resp && (resp.responseData || resp.data || resp)) || {}
        const raw = Array.isArray(data.reviews) ? data.reviews : []
        raw.forEach(r => out.push({ id: r._id, rating: Math.max(1, Math.min(5, Number(r.rating) || 5)), at: r.createdAt || null, google: r.source === 'google' }))
        const oldest = Math.min(...raw.map(r => time(r.createdAt) || Infinity))
        const more = data.pagination && data.pagination.hasMore != null ? !!data.pagination.hasMore : raw.length >= 20
        if (!raw.length || !more || (since && oldest < since.getTime())) break
        skip += raw.length
      }
    }
    return out
  })
}

// Bailey's front desk: whether it's answering, and conversations back to a
// date (`complete` false when ten pages didn't reach it)
export const loadFrontDesk = since => cached(`frontdesk:${day(since)}`, async () => {
  let settings
  try { settings = await loadSettings() } catch (e) { return null }
  const on = !!(settings && (settings.enabled || settings.voiceEnabled))
  const conversations = []
  let complete = false
  for (let page = 0; page < 10; page += 1) {
    let list
    // eslint-disable-next-line no-await-in-loop
    try { list = await loadConversations(page) } catch (e) { if (!page) return null; break }
    if (!list || !list.length) { complete = true; break }
    conversations.push(...list)
    const oldest = Math.min(...list.map(c => time(c.startedAt || c.at) || Infinity))
    if (since && oldest < since.getTime()) { complete = true; break }
  }
  return { on, used: on || conversations.length > 0, conversations, complete }
})

// Journeys, and the clients who have been through each
export const loadJourneys = () => cached('journeys', async () => {
  const list = await hydrateJourneys(localStorage.getItem('salonId'))
  const runs = {}
  await Promise.all((list || []).filter(j => isServerId(j.id)).map(j => marketingBackend.journeyRuns(j.id, 500)
    .then((r) => { runs[j.id] = Array.isArray(r) ? r : (r && r.runs) || [] })
    .catch(() => { runs[j.id] = [] })))
  return { list: list || [], runs }
})

// The loyalty scheme, and its stats once the server reports them
export const loadLoyalty = () => cached('loyalty', async () => {
  const [scheme, stats] = await Promise.all([
    hydrateLoyalty(currentOwnerId()).then(r => (r && r.scheme) || null).catch(() => null),
    getSchemeStats(),
  ])
  return { scheme, stats }
})

// For posted free times whose day has passed: how many of those times were
// booked afterwards, from the diary (the dozen most recent posts)
export const loadFreeTimesBooked = items => cached(`freetimes:${items.map(i => i.id).join(',')}`, async () => {
  let posted = 0
  let booked = 0
  let posts = 0
  // eslint-disable-next-line no-restricted-syntax
  for (const it of items.slice(0, 12)) {
    // eslint-disable-next-line no-await-in-loop
    const r = await checkTimes(it.date, it.times, it.staffId)
    if (r) { posts += 1; posted += it.times.length; booked += r.gone.length }
  }
  return posts ? { posted, booked, posts } : null
}, 10 * MINUTE)
